import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, Switch, TextField, Button,
    Divider, FormControlLabel, CircularProgress, Snackbar, Alert, Stack
} from '@mui/material';
import axiosInstance from '../../../services/axiosInstance';

const ScheduleSettings = () => {
    const userId = localStorage.getItem("userId") || "";
    const [schedule, setSchedule] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState({ open: false, type: 'success', text: '' });
    const [calendarStatus, setCalendarStatus] = useState({ loading: true, isConnected: false });
    const [outlookStatus, setOutlookStatus] = useState({ loading: true, isConnected: false });

    useEffect(() => {
        fetchSchedule();
        fetchCalendarStatus();
        fetchOutlookStatus();
    }, []);

    const fetchSchedule = async () => {
        try {
            const res = await axiosInstance.get(`/appointments/schedule`, {
                params: { userId }
            });
            if (res.data.success) {
                setSchedule(res.data.schedule);
            }
        } catch (error) {
            console.error("Error fetching schedule", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCalendarStatus = async () => {
        try {
            const res = await axiosInstance.get(`/calendar/status`, {
                params: { userId }
            });
            if (res.data.success) {
                setCalendarStatus({ loading: false, isConnected: res.data.isConnected });
            }
        } catch (error) {
            console.error("Error fetching calendar status", error);
            setCalendarStatus({ loading: false, isConnected: false });
        }
    };

    const fetchOutlookStatus = async () => {
        try {
            const res = await axiosInstance.get(`/outlook-calendar/status`, {
                params: { userId }
            });
            if (res.data.success) {
                setOutlookStatus({ loading: false, isConnected: res.data.isConnected });
            }
        } catch (error) {
            console.error("Error fetching outlook status", error);
            setOutlookStatus({ loading: false, isConnected: false });
        }
    };

    const handleDayChange = (index, field, value) => {
        const newAvail = [...schedule.availability];
        if (field === 'time') {
            newAvail[index].slots[0] = { ...newAvail[index].slots[0], ...value };
        } else {
            newAvail[index][field] = value;

            // If activating day and no slots or empty times, set default 09:00-17:00
            if (field === 'isActive' && value === true) {
                const currentSlot = newAvail[index].slots[0];
                if (!currentSlot || !currentSlot.startTime || !currentSlot.endTime) {
                    newAvail[index].slots = [{ startTime: '09:00', endTime: '17:00', _id: currentSlot?._id }];
                }
            }
        }
        setSchedule({ ...schedule, availability: newAvail });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await axiosInstance.post(`/appointments/schedule`, {
                userId,
                scheduleId: schedule._id,
                availability: schedule.availability,
                slotDuration: schedule.slotDuration,
                gapBetweenSlots: schedule.gapBetweenSlots
            });

            if (res.data.success) {
                setSchedule(res.data.schedule);
                setMsg({ open: true, type: 'success', text: 'Schedule updated successfully' });
            }
        } catch (error) {
            setMsg({ open: true, type: 'error', text: 'Failed to update schedule' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Box p={4} display="flex" justifyContent="center"><CircularProgress /></Box>;
    if (!schedule) return <Typography>No schedule found.</Typography>;

    const handleConnectGoogle = async () => {
        try {
            const res = await axiosInstance.get('/calendar/auth-url');
            if (res.data.success) {
                window.location.href = res.data.url;
            }
        } catch (error) {
            console.error("Error getting auth url", error);
            setMsg({ open: true, type: 'error', text: 'Failed to initiate Google connection' });
        }
    };

    const handleDisconnectGoogle = async () => {
        try {
            await axiosInstance.post('/calendar/disconnect', { userId });
            setCalendarStatus({ loading: false, isConnected: false });
            setMsg({ open: true, type: 'success', text: 'Google Calendar disconnected successfully' });
        } catch (error) {
            console.error("Error disconnecting calendar", error);
            setMsg({ open: true, type: 'error', text: 'Failed to disconnect Google Calendar' });
        }
    };

    const handleConnectOutlook = async () => {
        try {
            const res = await axiosInstance.get('/outlook-calendar/auth-url');
            if (res.data.success) {
                window.location.href = res.data.url;
            }
        } catch (error) {
            console.error("Error getting outlook auth url", error);
            setMsg({ open: true, type: 'error', text: 'Failed to initiate Outlook connection' });
        }
    };

    const handleDisconnectOutlook = async () => {
        try {
            await axiosInstance.post('/outlook-calendar/disconnect', { userId });
            setOutlookStatus({ loading: false, isConnected: false });
            setMsg({ open: true, type: 'success', text: 'Outlook Calendar disconnected successfully' });
        } catch (error) {
            console.error("Error disconnecting outlook", error);
            setMsg({ open: true, type: 'error', text: 'Failed to disconnect Outlook Calendar' });
        }
    };

    return (
        <Box sx={{ width: '100%', p: { xs: 2, md: 4 } }}>

            {/* Page Header */}
            <Box mb={5} display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography variant="h4" fontWeight={800} gutterBottom sx={{ color: '#1a1a1a', letterSpacing: '-0.5px' }}>
                        Schedule Settings
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Manage your availability and calendar integrations.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    size="large"
                    sx={{
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 700,
                        boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)',
                        px: 4
                    }}
                >
                    {saving ? 'Saving...' : 'Save Changes'}
                </Button>
            </Box>

            <Stack spacing={4}>

                {/* General Settings Card */}
                <Paper elevation={0} sx={{ p: 4, borderRadius: 4, border: '1px solid #eaecf0' }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 3 }}>
                        General Preferences
                    </Typography>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Slot Duration (minutes)"
                                type="number"
                                fullWidth
                                variant="outlined"
                                value={schedule.slotDuration}
                                onChange={(e) => setSchedule({ ...schedule, slotDuration: parseInt(e.target.value) })}
                                InputProps={{ sx: { borderRadius: 2 } }}
                                helperText="Length of each appointment slot."
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Gap Between Slots (minutes)"
                                type="number"
                                fullWidth
                                variant="outlined"
                                value={schedule.gapBetweenSlots || 0}
                                onChange={(e) => setSchedule({ ...schedule, gapBetweenSlots: parseInt(e.target.value) })}
                                InputProps={{ sx: { borderRadius: 2 } }}
                                helperText="Buffer time between appointments."
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Weekly Schedule Card */}
                <Paper elevation={0} sx={{ p: 4, borderRadius: 4, border: '1px solid #eaecf0' }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 1 }}>
                        Weekly Availability
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                        Set your recurring availability for each day of the week.
                    </Typography>

                    <Stack spacing={0} divider={<Divider sx={{ borderStyle: 'dashed' }} />}>
                        {schedule.availability.map((day, index) => (
                            <Box key={day.day} sx={{ py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>

                                {/* Day Toggle */}
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={day.isActive}
                                            onChange={(e) => handleDayChange(index, 'isActive', e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Typography sx={{ fontWeight: 600, textTransform: 'capitalize', width: 100, fontSize: '1.05rem' }}>
                                            {day.day}
                                        </Typography>
                                    }
                                />

                                {/* Time Selection */}
                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                                    {day.isActive ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#f9f9f9', p: 1, borderRadius: 2, border: '1px solid #eee' }}>
                                            <TextField
                                                type="time"
                                                variant="standard"
                                                InputProps={{ disableUnderline: true, sx: { fontSize: '0.95rem', fontWeight: 500 } }}
                                                value={day.slots[0]?.startTime || '09:00'}
                                                onChange={(e) => handleDayChange(index, 'time', { startTime: e.target.value })}
                                            />
                                            <Typography sx={{ color: 'text.disabled' }}>—</Typography>
                                            <TextField
                                                type="time"
                                                variant="standard"
                                                InputProps={{ disableUnderline: true, sx: { fontSize: '0.95rem', fontWeight: 500 } }}
                                                value={day.slots[0]?.endTime || '17:00'}
                                                onChange={(e) => handleDayChange(index, 'time', { endTime: e.target.value })}
                                            />
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', pr: 2 }}>
                                            Unavailable
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                </Paper>

                {/* Integrations Card */}
                <Paper elevation={0} sx={{ p: 4, borderRadius: 4, border: '1px solid #eaecf0', background: 'linear-gradient(to right, #fff, #f8faff)' }}>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 4 }}>Calendar Integrations</Typography>

                    <Stack spacing={4} divider={<Divider />}>
                        {/* Google Calendar */}
                        <Box>
                            <Box display="flex" alignItems="center" gap={2} mb={3}>
                                <img src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg" alt="Google Calendar" width="40" />
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={700}>Google Calendar</Typography>
                                    <Typography variant="body2" color="text.secondary">Sync appointments and prevent double bookings.</Typography>
                                </Box>
                            </Box>

                            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <Box sx={{
                                        width: 10, height: 10, borderRadius: '50%',
                                        bgcolor: calendarStatus.isConnected ? '#22c55e' : '#ef4444',
                                        boxShadow: calendarStatus.isConnected ? '0 0 0 4px #dcfce7' : '0 0 0 4px #fee2e2'
                                    }} />
                                    <Typography variant="body2" fontWeight={600} color={calendarStatus.isConnected ? 'success.main' : 'error.main'}>
                                        {calendarStatus.loading ? 'Checking status...' : (calendarStatus.isConnected ? 'Connected & Syncing' : 'Not Connected')}
                                    </Typography>
                                </Box>

                                <Box>
                                    {calendarStatus.isConnected ? (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            onClick={handleDisconnectGoogle}
                                            disabled={calendarStatus.loading}
                                            sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
                                        >
                                            Disconnect
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            onClick={handleConnectGoogle}
                                            disabled={calendarStatus.loading}
                                            sx={{ borderRadius: 2, textTransform: 'none', px: 3, bgcolor: '#4285F4', '&:hover': { bgcolor: '#3367D6' } }}
                                        >
                                            Connect Account
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        {/* Outlook Calendar */}
                        <Box>
                            <Box display="flex" alignItems="center" gap={2} mb={3}>
                                <img src="https://upload.wikimedia.org/wikipedia/commons/d/df/Microsoft_Office_Outlook_%282018%E2%80%93present%29.svg" alt="Outlook Calendar" width="40" />
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={700}>Outlook Calendar</Typography>
                                    <Typography variant="body2" color="text.secondary">Connect your Microsoft 365 or Outlook.com account.</Typography>
                                </Box>
                            </Box>

                            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <Box sx={{
                                        width: 10, height: 10, borderRadius: '50%',
                                        bgcolor: outlookStatus.isConnected ? '#22c55e' : '#ef4444',
                                        boxShadow: outlookStatus.isConnected ? '0 0 0 4px #dcfce7' : '0 0 0 4px #fee2e2'
                                    }} />
                                    <Typography variant="body2" fontWeight={600} color={outlookStatus.isConnected ? 'success.main' : 'error.main'}>
                                        {outlookStatus.loading ? 'Checking status...' : (outlookStatus.isConnected ? 'Connected & Syncing' : 'Not Connected')}
                                    </Typography>
                                </Box>

                                <Box>
                                    {outlookStatus.isConnected ? (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            onClick={handleDisconnectOutlook}
                                            disabled={outlookStatus.loading}
                                            sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
                                        >
                                            Disconnect
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            onClick={handleConnectOutlook}
                                            disabled={outlookStatus.loading}
                                            sx={{ borderRadius: 2, textTransform: 'none', px: 3, bgcolor: '#0078d4', '&:hover': { bgcolor: '#005a9e' } }}
                                        >
                                            Connect Account
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Stack>
                </Paper>
            </Stack>

            <Snackbar open={msg.open} autoHideDuration={6000} onClose={() => setMsg({ ...msg, open: false })}>
                <Alert severity={msg.type} sx={{ width: '100%', borderRadius: 2, boxShadow: 3 }}>{msg.text}</Alert>
            </Snackbar>
        </Box>
    );
};

export default ScheduleSettings;
